import Button from '@jetbrains/ring-ui/components/button/button'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import classNames from 'classnames'
import {memo, Suspense, useCallback, useMemo} from 'react'

import {useIsAutodetectTimeZone} from '../../../hooks/useIsAutodetectTimeZone'
import {useIsHighlightMyChangesAndInvestigations} from '../../../hooks/useIsHighlightMyChangesAndInvestigations'
import useMyId from '../../../hooks/useMyId'
import {usePermission} from '../../../hooks/usePermission'
import {getProjectsHash} from '../../../selectors'
import type {BuildTypeId, Investigation, ProjectId} from '../../../types'
import {InvestigationStates, Permission, stringifyId} from '../../../types'
import {emptyArray, noop} from '../../../utils/empty'
import Icon from '../Icon/Icon'
import IconButton from '../IconButton/IconButton'
import Popup from '../Popup/Popup.lazy'

import InvestigationDetails from './InvestigationDetails'
import {getInvestigationInCurrentScope} from './Investigations.selectors'
import {getActiveInvestigations, getAssigneeName, getFilteredEntities} from './Investigations.utils'

import styles from './Investigations.css'

import {useAppSelector} from 'src/hooks/react-redux'

const directions = [Directions.BOTTOM_RIGHT, Directions.TOP_RIGHT]

type Props = {
  className?: string
  iconClassName?: string
  projectId: ProjectId | null | undefined
  buildTypeId: BuildTypeId | null | undefined
  currentlyInvestigated: boolean | null | undefined
  readonly investigations: ReadonlyArray<Investigation>
  isShowOnlyIcon?: boolean
  onAssignInvestigation?: (presetFix?: boolean) => unknown
  onShowInvestigationHistory?: () => unknown
}

type ContainerProps = {
  isHighlightMyInvestigations: boolean
  isAutodetectTimeZone: boolean
}

function InvestigationsComponent({
  className,
  iconClassName,
  projectId,
  buildTypeId,
  currentlyInvestigated,
  investigations = emptyArray,
  isShowOnlyIcon = false,
  onAssignInvestigation = noop,
  onShowInvestigationHistory,
  isHighlightMyInvestigations,
  isAutodetectTimeZone,
}: Props & ContainerProps) {
  const currentUserId = useMyId()
  const hasPermission = usePermission(Permission.ASSIGN_INVESTIGATION, projectId)

  const handleAssignInvestigation = useCallback(
    () => onAssignInvestigation(),
    [onAssignInvestigation],
  )
  const handleFixInvestigation = useCallback(
    () => onAssignInvestigation(true),
    [onAssignInvestigation],
  )
  const getIsAssignedToCurrentUser = useCallback(
    (investigation: Investigation) => investigation.assignee.id === currentUserId,
    [currentUserId],
  )
  const activeInvestigations = useMemo(
    () => getActiveInvestigations(investigations),
    [investigations],
  )
  const projectsHash = useAppSelector(getProjectsHash)
  const investigationInCurrentScope = useAppSelector(state =>
    getInvestigationInCurrentScope(state, activeInvestigations, projectId, buildTypeId),
  )
  const filteredInvestigations = useMemo(
    () => getFilteredEntities(projectsHash, activeInvestigations, projectId, buildTypeId),
    [buildTypeId, activeInvestigations, projectId, projectsHash],
  )
  const {state} = investigationInCurrentScope ?? {}
  const popup = useMemo(
    () => (
      <Popup
        className={classNames(styles.popup, {
          [styles.hasPermission]: hasPermission,
          [styles.withSeveralInvestigations]: filteredInvestigations.length > 1,
        })}
        directions={directions}
      >
        <div data-test-investigation-popup className={styles.body}>
          <div className={styles.content}>
            {filteredInvestigations.map(item => (
              <div key={stringifyId(item.id)} className={styles.investigationDetails}>
                <InvestigationDetails
                  buildTypeId={buildTypeId}
                  currentUserId={currentUserId}
                  investigation={item}
                  isAutodetectTimeZone={isAutodetectTimeZone}
                />
              </div>
            ))}
          </div>
          {hasPermission && (
            <div className={styles.footer}>
              <Button className={styles.actionButton} onClick={handleAssignInvestigation}>
                {'Reassign...'}
              </Button>
              {state !== InvestigationStates.FIXED && (
                <Button className={styles.actionButton} onClick={handleFixInvestigation}>
                  {'Fix...'}
                </Button>
              )}
              {onShowInvestigationHistory != null && (
                <Button className={styles.actionButton} onClick={onShowInvestigationHistory}>
                  {'Show history'}
                </Button>
              )}
            </div>
          )}
        </div>
      </Popup>
    ),
    [
      buildTypeId,
      currentUserId,
      filteredInvestigations,
      handleAssignInvestigation,
      handleFixInvestigation,
      hasPermission,
      isAutodetectTimeZone,
      onShowInvestigationHistory,
      state,
    ],
  )

  if (investigationInCurrentScope == null && hasPermission && isShowOnlyIcon === false) {
    return (
      <div className={classNames(className, styles.wrapper)} data-test-investigation>
        <IconButton
          className={styles.assignAction}
          icon="investigation"
          title=""
          onClick={handleAssignInvestigation}
        >
          {'Assign investigation...'}
        </IconButton>
      </div>
    )
  }

  if (currentlyInvestigated === true || state === InvestigationStates.FIXED) {
    if (investigationInCurrentScope == null) {
      return null
    }

    const isAssignedToCurrentUser = getIsAssignedToCurrentUser(investigationInCurrentScope)
    const assigneeName = getAssigneeName(investigationInCurrentScope, isAssignedToCurrentUser)
    let content = `Investigation assigned to ${assigneeName}`

    if (state === InvestigationStates.FIXED) {
      content = `Investigation fixed by ${assigneeName}`
    }

    const iconClasses = classNames(iconClassName, styles.icon, {
      [styles.assignedToMe]:
        state === InvestigationStates.TAKEN &&
        isAssignedToCurrentUser &&
        isHighlightMyInvestigations,
      [styles.fixed]: state === InvestigationStates.FIXED,
    })
    const anchor = (
      <Button className={styles.anchor} aria-label="investigation" text>
        <div className={styles.anchorContent}>
          <Icon className={iconClasses} name="investigation" />
          {isShowOnlyIcon !== true && (
            <>
              <span className={styles.anchorText}>
                {investigations.length === 1 ? content : 'Investigations'}
              </span>
              <Icon className={styles.anchorChevron} name="chevron-10px" />
            </>
          )}
        </div>
      </Button>
    )
    return (
      <div className={classNames(className, styles.wrapper)} data-test-investigation>
        <Dropdown className={styles.dropdown} anchor={anchor} hoverMode>
          {popup}
        </Dropdown>
      </div>
    )
  }

  return null
}

const Investigations = memo(InvestigationsComponent)

function InvestigationsContainer(props: Props) {
  const isHighlightMyInvestigations = useIsHighlightMyChangesAndInvestigations()
  const isAutodetectTimeZone = useIsAutodetectTimeZone()

  return (
    <Suspense>
      <Investigations
        {...props}
        isHighlightMyInvestigations={isHighlightMyInvestigations}
        isAutodetectTimeZone={isAutodetectTimeZone}
      />
    </Suspense>
  )
}

export default memo(InvestigationsContainer)
